import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Skeleton, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { formatInTz } from '@repletoryreach/sdk-utils/date';
import { media, channelWa } from '@repletoryreach/sdk-api';
import type { ConversationMessage } from '@repletoryreach/sdk-api/inbox';
import { openFileViewer } from '@repletoryreach/sdk-ui/file-viewer';
import { ScrollArea, type Glide } from '@repletoryreach/sdk-ui/scroll';
import { formatBytes, mediaViewerSource } from '@repletoryreach/sdk-media';
import { WhatsAppText } from '@repletoryreach/sdk-utils/whatsapp-text';
import { useT, useI18n } from '../../../i18n/I18nProvider';

export interface MessageThreadProps {
  messages: ConversationMessage[];
  loading: boolean;
  error: unknown;
  timezone: string;
  /** Locally-appended messages awaiting the server's copy. */
  pending?: PendingMessage[];
  /**
   * Send the same text again, for the one failure worth retrying. Offered
   * only where a retry could plausibly work: see {@link failureOf}.
   */
  onRetry?: (body: string) => void;
  /** Open the details panel on one message. */
  onOpenDetails?: (message: ConversationMessage) => void;
  /** The message the details panel is currently showing, so the bubble can say so. */
  detailsForId?: string | null;
  /** The conversation's channel. An email thread's messages are letters: plain text, subjects shown. */
  channel?: string | null;
  /** An email thread's subject, already in the header: a message repeats a subject only when it differs. */
  threadSubject?: string | null;
  /** Who Meta bills for the conversation's number: on the business's own account a billing refusal is theirs to fix. */
  metaPayer?: channelWa.MetaBillingPayer;
}

export interface PendingMessage {
  key: string;
  body: string;
  failed?: string | null;
  onRetry?: () => void;
}

/**
 * The conversation thread.
 *
 * `inbox.md` §4 item 1 called the scroll position "the single worst defect on
 * the page": `.rr-inbox2__messages` is `overflow-y: auto` and nothing ever
 * scrolled it, so opening a conversation landed on the OLDEST of the last 100
 * messages and new arrivals appeared off-screen. The scroll engine now does
 * what this component used to hand-roll: `stickToBottom="smart"` keeps the
 * view at the bottom only while the agent is already there, so reading back
 * through history is not yanked away by an inbound message, and
 * `preserveScrollPosition` keeps the reader in place when older history is
 * loaded above. Jump to latest is the engine's own scroll.
 *
 * Also here: day separators, per-message failure (§4 item 4, now that
 * `services/inbox` actually projects `failed_at`), and times that are times
 * rather than full dates (§4 item 22).
 */
export function MessageThread({
  messages, loading, error, timezone, pending = [], onRetry, onOpenDetails, detailsForId, channel, threadSubject, metaPayer
}: MessageThreadProps) {
  const t = useT();
  const { locale } = useI18n();
  const glide = useRef<Glide | null>(null);
  const [showJump, setShowJump] = useState(false);

  const rows = useMemo(() => withDaySeparators(messages, locale, timezone), [messages, locale, timezone]);
  const isEmail = channel === 'email';
  /** Which email messages carry a subject that differs from the one before (the thread's, at first). */
  const subjectChanges = useMemo(() => (isEmail ? changedSubjects(messages, threadSubject ?? null) : new Map<string, string>()),
    [isEmail, messages, threadSubject]);

  // A conversation change always starts at the bottom, whatever the last one
  // was doing.
  const firstId = messages[0]?.id;
  useEffect(() => {
    glide.current?.scrollToBottom('instant');
    setShowJump(false);
  }, [firstId]);

  function jumpToLatest() {
    glide.current?.scrollToBottom('smooth');
    setShowJump(false);
  }

  if (error) {
    return (
      <div className="rr-inbox2__messages">
        <Alert color="error" role="alert">{t('inbox.messages.error')}</Alert>
      </div>
    );
  }

  return (
    <div className="rr-inbox2__thread-scroll" data-guide="inbox-thread">
      <ScrollArea
        className="rr-inbox2__messages"
        viewportClassName="rr-inbox2__messages-viewport"
        axis="y"
        stickToBottom="smart"
        preserveScrollPosition
        overscroll="contain"
        glideRef={glide}
        onBottomStateChange={(atBottom) => setShowJump(!atBottom)}
      >
        {loading && (
          <div aria-busy="true" aria-label={t('inbox.loadingMessages')}>
            {[0, 1, 2].map((i) => (
              <div key={i} className={`rr-inbox2__msg rr-inbox2__msg--${i % 2 ? 'outbound' : 'inbound'}`}>
                <Skeleton height={14} width={i % 2 ? 180 : 220} />
              </div>
            ))}
          </div>
        )}

        {!loading && messages.length === 0 && pending.length === 0 && (
          <EmptyState title={t('inbox.messages.empty')} description={t('inbox.messages.empty.hint')} />
        )}

        {rows.map((row) =>
          row.kind === 'day' ? (
            <div key={row.key} className="rr-inbox2__day">
              <span>{row.label}</span>
            </div>
          ) : (
            <Bubble
              key={row.message.id}
              m={row.message}
              timezone={timezone}
              onRetry={onRetry}
              onOpenDetails={onOpenDetails}
              isDetailed={detailsForId === row.message.id}
              email={isEmail}
              subject={subjectChanges.get(row.message.id) ?? null}
              metaPayer={metaPayer}
            />
          )
        )}

        {pending.map((p) => (
          <div key={p.key} className="rr-inbox2__msg rr-inbox2__msg--outbound is-pending">
            <div className={`rr-inbox2__msg-body${isEmail ? ' rr-inbox2__msg-body--email' : ''}`}>
              {isEmail ? p.body : <WhatsAppText value={p.body} />}
            </div>
            <div className="rr-inbox2__msg-meta">
              {p.failed ? (
                <>
                  <span className="rr-inbox2__msg-failed">{p.failed}</span>
                  {p.onRetry && (
                    <button type="button" className="rr-inbox2__msg-retry" onClick={p.onRetry}>
                      {t('action.retry')}
                    </button>
                  )}
                </>
              ) : (
                t('inbox.message.sending')
              )}
            </div>
          </div>
        ))}
      </ScrollArea>

      {showJump && (
        <Button size="xs" className="rr-inbox2__jump" onClick={jumpToLatest}>
          {t('inbox.jumpToLatest')}
        </Button>
      )}
    </div>
  );
}

function Bubble({ m, timezone, onRetry, onOpenDetails, isDetailed, email = false, subject = null, metaPayer }: {
  m: ConversationMessage;
  timezone: string;
  onRetry?: (body: string) => void;
  onOpenDetails?: (message: ConversationMessage) => void;
  isDetailed?: boolean;
  /** A message in an email thread: plain text, line breaks kept, no WhatsApp formatting. */
  email?: boolean;
  /** The subject to show above it, when it differs from the one before. */
  subject?: string | null;
  metaPayer?: channelWa.MetaBillingPayer;
}) {
  const t = useT();
  const failed = !!m.failed_at;
  const time = formatInTz(m.created_at, { timeZone: timezone, year: undefined, month: undefined, day: undefined });

  /*
   * Why the reason is translated rather than repeated.
   *
   * This used to show `error_title` verbatim: Meta's own words, in English,
   * to a reader who may not be reading in English, and in Meta's register
   * rather than a shop's. "Business eligibility payment issue" is the exact
   * sentence Meta sends for 131042, and it does not tell the person holding
   * the phone that their WhatsApp account has no card on it or where to put
   * one. Meta's numeric code is the stable thing, so it is what the console
   * translates; Meta's own sentence stays on the send record and in the log.
   */
  const failure = failed ? failureOf(m) : null;
  const reason = failure
    ? failure.code === 'ACCOUNT_NOT_PAYABLE' && metaPayer === 'customer'
      ? t('inbox.message.failed.ACCOUNT_NOT_PAYABLE.customer')
      : t(`inbox.message.failed.${failure.code}`, m.error_title ?? t('inbox.message.failed'))
    : m.error_code
      ? t('inbox.message.failed.code', { code: m.error_code })
      : t('inbox.message.failed');
  // Said quietly, because a person looking at a failed message is already
  // worried about one thing and should not have to wonder about a second.
  // True either way: a message Meta refused after accepting is refunded, and
  // one that never reached Meta was never charged.
  const notCharged = failed ? t('inbox.message.failed.notCharged') : null;
  const retry = failed && failure?.retryable && onRetry && m.body ? () => onRetry(m.body!) : null;

  const hasFile = !!m.media_id || !!m.media_url;
  const isMediaKind = MEDIA_KINDS.has(m.msg_type);

  /*
   * A template message, as the recipient read it.
   *
   * `services/channel-wa` composes the wording from the template and the
   * parameter values at the moment of sending, so `body` is the message. What
   * stays structured is everything that is not words: a media header, which
   * needs a line written in THIS reader's language rather than one composed on
   * a server, and the buttons, which have to be drawn as buttons.
   *
   * The marker below the message keeps the one fact the wording no longer
   * carries: that this was a template and which one. Quiet on purpose. The
   * complaint that started this was a bubble reading
   * `[template: test_repletory_reach_response]`, and replacing one shout with
   * another would not be an improvement.
   */
  const facts = m.template_facts ?? null;
  const mediaHeader = facts?.header && facts.header.format !== 'TEXT' ? facts.header : null;
  const buttons = facts?.buttons ?? [];
  const wordingLost = m.msg_type === 'template' && !m.body;
  // A message WhatsApp did not pass on (18 October 2026): its webhook says only "unsupported", so there is no text
  // to show. Said in words, with what to do, rather than "(unsupported)".
  const unreadable = (m.msg_type === 'unsupported' || m.msg_type === 'unknown') && !m.body && !hasFile;

  return (
    <div className={`rr-inbox2__msg rr-inbox2__msg--${m.direction}${failed ? ' is-failed' : ''}${isDetailed ? ' is-detailed' : ''}`}>
      {subject && (
        <div className="rr-inbox2__msg-subject" data-testid="message-subject">
          {t('inbox.email.subjectLine', { subject })}
        </div>
      )}
      {mediaHeader && (
        <div className="rr-inbox2__tpl-header">
          <span className="rr-inbox2__attachment-icon" aria-hidden><KindIcon kind={mediaHeader.format.toLowerCase()} /></span>
          <span className="rr-inbox2__tpl-header-text">
            {t(`inbox.message.template.header.${mediaHeader.format}`, mediaHeader.format)}
          </span>
        </div>
      )}
      {hasFile && <Attachment m={m} />}
      {isMediaKind && !hasFile && (
        // Meta sent a media id and the file could not be fetched or kept:
        // say so rather than showing the word "image" as if it were the message.
        <div className="rr-inbox2__attachment rr-inbox2__attachment--missing">
          <span className="rr-inbox2__attachment-icon" aria-hidden><KindIcon kind={m.msg_type} /></span>
          <span className="rr-inbox2__attachment-name">{t(`inbox.attachment.kind.${m.msg_type}`, m.msg_type)}</span>
          <span className="rr-inbox2__attachment-sub">{t('inbox.attachment.unavailable')}</span>
        </div>
      )}
      {unreadable ? (
        <div className="rr-inbox2__msg-body rr-inbox2__msg-body--lost" data-testid="message-unsupported">
          <Text role="bodySmall" fw={600}>{t('inbox.message.unsupported.title')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('inbox.message.unsupported.body')}</Text>
        </div>
      ) : wordingLost ? (
        /*
          The wording of this one is genuinely gone: it went out before the
          platform stored what a template said, and the template itself is no
          longer on file. Saying so is the only honest thing available. A
          sentence invented here would be a sentence the customer never read,
          which is the same fault as the placeholder, dressed better.
        */
        <div className="rr-inbox2__msg-body rr-inbox2__msg-body--lost">
          <em>
            {m.template_name
              ? t('inbox.message.template.noWording.named', { name: m.template_name })
              : t('inbox.message.template.noWording')}
          </em>
        </div>
      ) : email && m.body ? (
        // An email is plain text as the customer wrote it: no WhatsApp
        // formatting (an underscore in an address stays one), line breaks
        // kept, and React escapes every character of it.
        <div className="rr-inbox2__msg-body rr-inbox2__msg-body--email">{m.body}</div>
      ) : (m.body || (!hasFile && !isMediaKind)) && (
        // WhatsApp's own formatting, not Markdown, and links found before the
        // formatting pass so an underscore inside a URL stays an underscore.
        // Meta's own onboarding messages are full of both.
        <div className="rr-inbox2__msg-body">
          <WhatsAppText value={m.body} fallback={<em>({m.msg_type})</em>} />
        </div>
      )}
      {/* What the chatbot made of the file (27 October 2026): a voice note's transcript, what a photo shows, or a
          document's first lines, so a person taking over knows what was said. After the caption: the customer's own words first. */}
      {m.media_text && (
        <p className="rr-inbox2__media-text" data-testid="message-media-text">
          <span className="rr-inbox2__media-text-label">
            {t(m.msg_type === 'audio' || m.msg_type === 'voice' ? 'inbox.mediaText.transcript'
              : m.msg_type === 'document' || m.msg_type === 'file' ? 'inbox.mediaText.document'
              : 'inbox.mediaText.photo')}
          </span>{' '}
          {m.media_text}
        </p>
      )}
      {m.msg_type === 'call' && m.meta_message_id?.startsWith('voice-call:') && (
        // The whole call (transcript, recording, cost) is on the Voice page.
        <a className="rr-inbox2__call-link" href={`/voice?call=${callIdOf(m.meta_message_id)}`}>{t('inbox.voice.openCall')}</a>
      )}
      {buttons.length > 0 && (
        // The buttons the recipient saw, drawn as buttons and inert here: they
        // are a record of what was sent, not controls for the agent to press.
        <ul className="rr-inbox2__tpl-buttons" aria-label={t('inbox.message.template.buttons')}>
          {buttons.map((b, i) => (
            <li key={`${b.text ?? b.type}-${i}`} className="rr-inbox2__tpl-button">
              <span className="rr-inbox2__tpl-button-icon" aria-hidden><ButtonIcon type={b.type} /></span>
              <span>{b.text ?? b.url ?? b.phone_number}</span>
            </li>
          ))}
        </ul>
      )}
      {m.template_name && (
        <div className="rr-inbox2__tpl-mark">
          {t('inbox.message.template.marker', { name: m.template_name })}
        </div>
      )}
      {failed && (
        /*
          A block of its own, under the message and above the timestamp row.
          The reason is a sentence now rather than a code, and the meta row is
          a right-aligned strip built for a time and two ticks: a sentence put
          in there wrapped into a ragged column against the right edge.
        */
        <div className="rr-inbox2__msg-failure" role="status">
          <span className="rr-inbox2__msg-failed">{reason}</span>
          {notCharged && <span className="rr-inbox2__msg-refunded">{notCharged}</span>}
          {retry && (
            <button type="button" className="rr-inbox2__msg-retry" onClick={retry}>
              {t('action.retry')}
            </button>
          )}
        </div>
      )}
      <div className="rr-inbox2__msg-meta">
        <Tooltip label={formatInTz(m.created_at, { timeZone: timezone })} position="top">
          <span>{time}</span>
        </Tooltip>
        {/*
          Outbound receipts are ticks, as they are on the handset the customer
          is holding, so the two views agree. The tick still carries a text
          alternative, because a shape and a colour are the whole signal
          otherwise.
        */}
        {/* An outbound row exists because it was accepted for sending, so one
            tick is the floor; there is no `sent_at` on the wire to gate it on. */}
        {!failed && m.direction === 'outbound' && (
          <Ticks read={!!m.read_at} delivered={!!m.delivered_at} />
        )}
        {onOpenDetails && (
          /*
            A real button in the meta row rather than a click on the bubble.
            The bubble holds links and an attachment tile that are themselves
            clickable, so making the whole thing a control would swallow them,
            and a div with an onClick is not reachable by keyboard at all.
          */
          <button
            type="button"
            className="rr-inbox2__msg-info"
            onClick={() => onOpenDetails(m)}
            aria-label={t('inbox.details.open')}
            aria-pressed={!!isDetailed}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <path d="M12 7.6v.6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/** Which kind of button this was on the handset. */
function ButtonIcon({ type }: { type: string }) {
  const common = { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (type === 'URL') return <svg {...common}><path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7" /></svg>;
  if (type === 'PHONE_NUMBER') return <svg {...common}><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8.1 9.8a16 16 0 006 6l1.3-1.2a2 2 0 012.1-.5c.9.3 1.8.6 2.7.7a2 2 0 011.8 2.1z" /></svg>;
  if (type === 'COPY_CODE') return <svg {...common}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>;
  return <svg {...common}><path d="M21 11.5a8.4 8.4 0 01-9 8.4 8.6 8.6 0 01-3.8-.9L3 20.5l1.5-4.8A8.4 8.4 0 1121 11.5z" /></svg>;
}

/**
 * What a failed bubble means, from Meta's numeric code.
 *
 * The mapping is the SDK's, so channel-wa, this console and the ledger line
 * that reverses the charge all use one vocabulary: `deliveryFailure` in
 * `sdk-api` mirrors `MetaRefusals.ForDelivery` in `services/channel-wa`.
 * Only the wording is this console's, and only because it has to be in the
 * reader's own language.
 */
function failureOf(m: ConversationMessage): { code: string; retryable: boolean } {
  const f = channelWa.deliveryFailure(m.error_code);
  return { code: f.code, retryable: f.retryable };
}

const MEDIA_KINDS = new Set(['image', 'video', 'audio', 'document', 'sticker']);

/** Object URLs already fetched this session, so scrolling back does not refetch a thumbnail. */
const thumbCache = new Map<string, string>();

/**
 * A stored image, as an object URL fetched with the session's token. The media
 * route is authenticated, so an `<img src>` straight at it would 401; the
 * bytes come through the SDK instead, once per file per session.
 */
function useThumbnail(mediaId: string | null | undefined, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(() => (mediaId && thumbCache.get(mediaId)) || null);
  useEffect(() => {
    if (!enabled || !mediaId) return;
    const cached = thumbCache.get(mediaId);
    if (cached) { setUrl(cached); return; }
    const ac = new AbortController();
    media.download(mediaId, ac.signal)
      .then((blob) => {
        const next = URL.createObjectURL(blob);
        thumbCache.set(mediaId, next);
        setUrl(next);
      })
      .catch(() => { /* the card still shows the name and View */ });
    return () => ac.abort();
  }, [mediaId, enabled]);
  return url;
}

/**
 * The file a message carried: a thumbnail for a picture, a tile for anything
 * else, the name and size, and View, which opens it in the file viewer.
 */
function Attachment({ m }: { m: ConversationMessage }) {
  const t = useT();
  const mime = m.media_mime ?? '';
  const kind = mime.startsWith('image/') || m.msg_type === 'image' || m.msg_type === 'sticker' ? 'image'
    : mime.startsWith('video/') || m.msg_type === 'video' ? 'video'
    : mime.startsWith('audio/') || m.msg_type === 'audio' ? 'audio'
    : 'document';
  const name = m.media_name ?? t(`inbox.attachment.kind.${m.msg_type}`, m.msg_type);
  const thumb = useThumbnail(m.media_id, kind === 'image' && !!m.media_id);
  const linkThumb = kind === 'image' && !m.media_id && m.media_url ? m.media_url : null;
  const [linkBroken, setLinkBroken] = useState(false);

  const open = () => {
    if (m.media_id) {
      openFileViewer(mediaViewerSource({ id: m.media_id, url: '', mime_type: mime || 'application/octet-stream', size_bytes: m.media_size ?? 0, original_name: m.media_name ?? null }, { title: name }));
    } else if (m.media_url) {
      openFileViewer({ kind: 'remote', url: m.media_url, name: m.media_name ?? undefined, title: name });
    }
  };

  const picture = thumb ?? (linkBroken ? null : linkThumb);

  return (
    <button type="button" className={`rr-inbox2__attachment rr-inbox2__attachment--${kind}`} onClick={open} title={t('inbox.attachment.open')}>
      {picture
        ? <img className="rr-inbox2__attachment-thumb" src={picture} alt="" onError={() => setLinkBroken(true)} />
        : <span className="rr-inbox2__attachment-icon" aria-hidden><KindIcon kind={kind} /></span>}
      <span className="rr-inbox2__attachment-text">
        <span className="rr-inbox2__attachment-name">{name}</span>
        <span className="rr-inbox2__attachment-sub">
          {[m.media_mime, m.media_size ? formatBytes(m.media_size) : null].filter(Boolean).join(' · ') || t('inbox.attachment.open')}
        </span>
      </span>
      <span className="rr-inbox2__attachment-cta">{t('inbox.attachment.open')}</span>
    </button>
  );
}

function KindIcon({ kind }: { kind: string }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (kind === 'image' || kind === 'sticker') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8.5" cy="10" r="1.6" /><path d="M4 18l5-5 3.5 3.5 3-3L20 18" /></svg>;
  if (kind === 'video') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3z" /></svg>;
  if (kind === 'audio') return <svg {...common}><path d="M9 17V6l10-2v11" /><circle cx="6.5" cy="17.5" r="2.5" /><circle cx="16.5" cy="15.5" r="2.5" /></svg>;
  return <svg {...common}><path d="M6 3h8l5 5v13H6z" /><path d="M14 3v5h5" /><path d="M9 12h6M9 15.5h6M9 19h3" /></svg>;
}

/** One tick sent, two delivered, two blue read. */
function Ticks({ read, delivered }: { read: boolean; delivered: boolean }) {
  const t = useT();
  const label = read ? t('inbox.message.read') : delivered ? t('inbox.message.delivered') : t('inbox.message.sent');
  return (
    <span className={`rr-inbox2__ticks${read ? ' is-read' : ''}`}>
      <svg width="16" height="11" viewBox="0 0 16 11" fill="none" stroke="currentColor"
        strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="1,6 4.2,9.2 9.6,2" />
        {(delivered || read) && <polyline points="6.4,9.2 11.8,2" />}
      </svg>
      <span className="rr-visually-hidden">{label}</span>
    </span>
  );
}

type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'msg'; message: ConversationMessage };

/** Groups the thread by calendar day in the tenant's timezone. */
function withDaySeparators(messages: ConversationMessage[], locale: string, timezone: string): Row[] {
  const rows: Row[] = [];
  let lastDay = '';
  for (const message of messages) {
    const day = formatInTz(message.created_at, {
      timeZone: timezone, locale, year: 'numeric', month: 'short', day: 'numeric',
      hour: undefined, minute: undefined
    });
    if (day !== lastDay) {
      rows.push({ kind: 'day', key: `day-${day}-${message.id}`, label: day });
      lastDay = day;
    }
    rows.push({ kind: 'msg', message });
  }
  return rows;
}

/** A subject with its reply and forward prefixes taken off, for telling whether it really changed. */
function bareSubject(subject: string): string {
  return subject.replace(/^(\s*(re|fwd?|aw|antw)\s*:\s*)+/i, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * The email messages whose subject differs from the one before them, with the
 * subject to show. The thread's own subject is the starting point, because the
 * header already says it; "Re:" and "Fwd:" do not count as a change.
 */
function changedSubjects(messages: ConversationMessage[], threadSubject: string | null): Map<string, string> {
  const shown = new Map<string, string>();
  let last = threadSubject ? bareSubject(threadSubject) : '';
  for (const m of messages) {
    const subject = m.subject?.trim();
    if (!subject) continue;
    const bare = bareSubject(subject);
    if (bare && bare !== last) shown.set(m.id, subject);
    last = bare || last;
  }
  return shown;
}

/** The voice call's id from the message marker `voice-call:<32 hex>`, in the usual dashed form. */
function callIdOf(marker: string): string {
  const hex = marker.slice('voice-call:'.length);
  return hex.length === 32 ? `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` : hex;
}
